// Photographs / maps / drawings for a BSCR section — cover, inline placement
// (insert after any specific field), drag-to-reorder, and the Annex 1 gallery.
import { useRef, useState } from "react";
import { ImagePlus, Loader2, Trash2, Pencil, Check, X, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiError } from "@/lib/api";

const fileUrl = (rid, imgId) => `${API}/bsc/${rid}/images/${imgId}/file`;
const END = 999;
const trunc = (t, n = 32) => (t && t.length > n ? t.slice(0, n) + "…" : t || "");

function anchorOptions(fields) {
    const opts = [{ v: 0, l: "Beginning of section" }];
    (fields || []).forEach((f, i) => opts.push({ v: i + 1, l: `After: ${trunc(f.label)}` }));
    opts.push({ v: END, l: "End of section" });
    return opts;
}

export default function ImagePanel({ rid, sectionCode, images = [], fields = [], canEdit, onChange, title, single = false, withAnchor = false }) {
    const items = (images || []).filter((i) => i.section_code === sectionCode)
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [anchor, setAnchor] = useState(END);
    const [editing, setEditing] = useState(null);
    const [labelDraft, setLabelDraft] = useState("");
    const [dragId, setDragId] = useState(null);
    const [overId, setOverId] = useState(null);
    const opts = anchorOptions(fields);

    async function onPick(e) {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) { toast.error("Image too large (max 10 MB)"); return; }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("section_code", sectionCode);
            fd.append("label", "");
            if (withAnchor) fd.append("anchor_index", String(anchor));
            await api.post(`/bsc/${rid}/images`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Image uploaded");
            onChange?.();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
        } finally { setBusy(false); }
    }

    async function saveLabel(img) {
        try {
            await api.patch(`/bsc/${rid}/images/${img.id}`, { label: labelDraft });
            toast.success("Label updated"); setEditing(null); onChange?.();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    }

    async function setImgAnchor(img, v) {
        try { await api.patch(`/bsc/${rid}/images/${img.id}`, { anchor_index: Number(v) }); onChange?.(); }
        catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    }

    async function remove(img) {
        if (!window.confirm("Remove this image?")) return;
        try { await api.delete(`/bsc/${rid}/images/${img.id}`); toast.success("Image removed"); onChange?.(); }
        catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    }

    async function onDrop(target) {
        if (!dragId || dragId === target.id) { setDragId(null); setOverId(null); return; }
        const ids = items.map((i) => i.id);
        const from = ids.indexOf(dragId);
        const to = ids.indexOf(target.id);
        if (from < 0 || to < 0) return;
        ids.splice(to, 0, ids.splice(from, 1)[0]);
        setDragId(null); setOverId(null);
        try { await api.post(`/bsc/${rid}/images/reorder`, { section_code: sectionCode, ordered_ids: ids }); onChange?.(); }
        catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Reorder failed"); }
    }

    const canAddMore = canEdit && (!single || items.length === 0);

    return (
        <div data-testid={`bscr-image-panel-${sectionCode}`} className="rounded-xl border border-[#EFEAE4] bg-[#FCFBF9] p-3">
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <span className="text-[12px] font-bold text-[#1F1F1F]">{title || "Photographs, maps & drawings"}</span>
                {canAddMore && (
                    <div className="flex items-center gap-2">
                        {withAnchor && (
                            <select value={anchor} onChange={(e) => setAnchor(Number(e.target.value))} data-testid={`bscr-image-anchor-select-${sectionCode}`}
                                className="text-[11px] border border-[#E5E0DA] rounded-md px-1.5 py-1 bg-white text-[#6F6A64] max-w-[180px]">
                                {opts.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                            </select>
                        )}
                        <button data-testid={`bscr-image-upload-${sectionCode}`} onClick={() => inputRef.current?.click()} disabled={busy}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                            {busy ? <Loader2 size={12} className="animate-spin" /> : <ImagePlus size={12} />} {single ? "Upload image" : "Add image"}
                        </button>
                    </div>
                )}
                <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={onPick} data-testid={`bscr-image-input-${sectionCode}`} />
            </div>

            {items.length === 0 ? (
                <p className="text-[11.5px] italic text-[#9A968F]">{single ? "No cover image yet. Upload a building photo to appear on the report's front page." : "No images added yet. Upload photos, maps or drawings — choose exactly where each one sits, then drag to reorder."}</p>
            ) : (
                <div className={single ? "" : "grid sm:grid-cols-2 gap-3"}>
                    {items.map((img) => (
                        <div key={img.id} data-testid={`bscr-image-${img.id}`}
                            draggable={canEdit && !single && editing !== img.id}
                            onDragStart={() => setDragId(img.id)}
                            onDragOver={(e) => { e.preventDefault(); setOverId(img.id); }}
                            onDrop={() => onDrop(img)}
                            onDragEnd={() => { setDragId(null); setOverId(null); }}
                            className={`rounded-lg border bg-white overflow-hidden transition ${overId === img.id && dragId !== img.id ? "border-[var(--brand-accent,#C75A1B)] ring-1 ring-[var(--brand-accent,#C75A1B)]" : "border-[#E5E0DA]"} ${dragId === img.id ? "opacity-50" : ""}`}>
                            <img src={fileUrl(rid, img.id)} alt={img.label || "BSCR image"} className="w-full object-cover" style={{ maxHeight: single ? 240 : 160 }} />
                            <div className="px-2 py-1.5 flex items-center gap-1.5">
                                {canEdit && !single && <GripVertical size={13} className="text-[#C2BCB4] cursor-grab shrink-0" data-testid={`bscr-image-drag-${img.id}`} />}
                                {editing === img.id ? (
                                    <>
                                        <input autoFocus value={labelDraft} onChange={(e) => setLabelDraft(e.target.value)}
                                            placeholder="Label (e.g. North Elevation)" className="flex-1 text-[12px] border border-[#E5E0DA] rounded-md px-2 py-1 outline-none"
                                            data-testid={`bscr-image-label-input-${img.id}`} onKeyDown={(e) => e.key === "Enter" && saveLabel(img)} />
                                        <button onClick={() => saveLabel(img)} data-testid={`bscr-image-label-save-${img.id}`} className="text-[#3F7D3A]"><Check size={15} /></button>
                                        <button onClick={() => setEditing(null)} className="text-[#9A968F]"><X size={15} /></button>
                                    </>
                                ) : (
                                    <>
                                        <span className="flex-1 text-[12px] text-[#1F1F1F] truncate">{img.label || <span className="italic text-[#9A968F]">No label</span>}</span>
                                        {canEdit && (
                                            <>
                                                <button onClick={() => { setEditing(img.id); setLabelDraft(img.label || ""); }} data-testid={`bscr-image-edit-${img.id}`} className="text-[#6F6A64] hover:text-[var(--brand-accent,#C75A1B)]"><Pencil size={13} /></button>
                                                <button onClick={() => remove(img)} data-testid={`bscr-image-delete-${img.id}`} className="text-[#B33A2E]"><Trash2 size={13} /></button>
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                            {canEdit && withAnchor && editing !== img.id && (
                                <div className="px-2 pb-2 -mt-0.5">
                                    <select value={img.anchor_index ?? END} onChange={(e) => setImgAnchor(img, e.target.value)} data-testid={`bscr-image-anchor-${img.id}`}
                                        className="w-full text-[10.5px] border border-[#EFEAE4] rounded-md px-1.5 py-1 bg-[#FCFBF9] text-[#6F6A64]">
                                        {opts.map((o) => <option key={o.v} value={o.v}>Place: {o.l}</option>)}
                                    </select>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
